// SiteHeader — sticky top nav matching WebNav from web-screens.jsx.
// Transparent over hero, becomes frosted dark bg on scroll.
//
// Responsive: at ≤768px the 4 nav tabs + AI button collapse into a hamburger
// that opens a fullscreen drawer with the same destinations. Logo + auth
// button (sign-in / avatar) stay in the bar at all widths.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
// Logo asset — full brand mark (icon + wordmark in one). Light fill, designed for
// dark surfaces. Source: docs/globalflyt-logo-dark.svg.
import logoUrl from '@/assets/globalflyt-logo.svg';

const NAV_TABS = [
  { key: 'discover', label: 'Discover', path: '/' },
  { key: 'flights',  label: 'Flights',  path: '/search' },
  { key: 'hotels',   label: 'Hotels',   path: '/hotels' },
  { key: 'trips',    label: 'My Trips', path: '/account' },
] as const;

const MOBILE_BP = 768; // px — break to hamburger below this width

interface SiteHeaderProps {
  onOpenAI?: () => void;
}

const fontStack = '"Inter Variable", "Inter", system-ui, sans-serif';

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BP : false,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BP}px)`);
    const fn = (e: MediaQueryListEvent | MediaQueryList): void => setIsMobile(e.matches);
    fn(mq);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return isMobile;
};

export function SiteHeader({ onOpenAI }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { status, user, logout } = useAuth();
  const isMobile = useIsMobile();

  useEffect(() => {
    const fn = (): void => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  // Close drawer on route change.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const activeKey = NAV_TABS.find(t =>
    t.path === '/' ? location.pathname === '/' : location.pathname.startsWith(t.path),
  )?.key ?? 'discover';

  const navInk    = '#F4F3F0';
  const navInkDim = 'rgba(255,255,255,0.65)';

  const goAI = onOpenAI ?? (() => navigate('/ai'));

  return (
    <>
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          padding: 'clamp(12px, 3vw, 14px) clamp(16px, 4vw, 32px)',
          background: scrolled ? 'rgba(10,12,16,0.85)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: `1px solid ${scrolled ? 'rgba(255,255,255,0.07)' : 'transparent'}`,
          transition: 'background .25s ease, border-color .25s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(12px, 2vw, 24px)',
        }}
      >
        {/* Logo — full brand mark (icon + wordmark in one SVG) */}
        <button
          onClick={() => navigate('/')}
          aria-label="Global Flyt — home"
          style={{
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          <img
            src={logoUrl}
            alt="Global Flyt"
            style={{
              // Square SVG; height drives sizing. Width auto-scales.
              height: 'clamp(32px, 5vw, 40px)',
              width: 'auto',
              display: 'block',
            }}
          />
        </button>

        {/* Desktop nav tabs — hidden on mobile */}
        {!isMobile && (
          <nav style={{ display: 'flex', gap: 4, marginLeft: 24 }}>
            {NAV_TABS.map(t => {
              const active = activeKey === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => navigate(t.path)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 999,
                    background: active
                      ? scrolled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.18)'
                      : 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: fontStack,
                    fontSize: 14,
                    fontWeight: 500,
                    color: active ? navInk : navInkDim,
                    transition: 'all .15s',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </nav>
        )}

        <div style={{ flex: 1 }} />

        {/* Desktop-only AI pill */}
        {!isMobile && (
          <button
            onClick={goAI}
            style={{
              padding: '10px 16px',
              borderRadius: 999,
              background: 'transparent',
              color: navInk,
              border: `1px solid ${scrolled ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.3)'}`,
              cursor: 'pointer',
              fontFamily: fontStack,
              fontSize: 13,
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              transition: 'border-color .25s',
              whiteSpace: 'nowrap',
            }}
          >
            <span style={{
              width: 18, height: 18, borderRadius: 9,
              background: 'linear-gradient(135deg, #E8441A, #4B8EDB)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10,
            }}>✦</span>
            Global Flyt AI
          </button>
        )}

        {/* Auth button — always visible */}
        {status === 'authed' && user ? (
          <button
            onClick={() => navigate('/account')}
            aria-label="Account"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#1B3A6B',
              border: `1.5px solid ${scrolled ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.35)'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#F4F3F0',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'border-color .25s',
              flexShrink: 0,
            }}
          >
            {(user.fullName ?? user.email ?? 'U').charAt(0).toUpperCase()}
          </button>
        ) : (
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: 'clamp(8px, 2vw, 10px) clamp(14px, 3vw, 18px)',
              borderRadius: 999,
              background: '#E8441A',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
              fontFamily: fontStack,
              fontSize: 13,
              fontWeight: 600,
              transition: 'background .15s',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Sign in
          </button>
        )}

        {/* Hamburger — mobile only */}
        {isMobile && (
          <button
            onClick={() => setDrawerOpen(o => !o)}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            style={{
              width: 36, height: 36,
              borderRadius: 8,
              background: 'transparent',
              border: `1px solid ${scrolled ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.3)'}`,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              {drawerOpen ? (
                <path d="M6 6l12 12M6 18L18 6" stroke={navInk} strokeWidth="2" strokeLinecap="round" />
              ) : (
                <>
                  <path d="M3 7h18" stroke={navInk} strokeWidth="2" strokeLinecap="round" />
                  <path d="M3 12h18" stroke={navInk} strokeWidth="2" strokeLinecap="round" />
                  <path d="M3 17h18" stroke={navInk} strokeWidth="2" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
        )}
      </header>

      {/* Mobile drawer */}
      {isMobile && drawerOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          style={{
            position: 'fixed',
            inset: 0,
            top: 'clamp(56px, 12vw, 68px)',
            background: 'rgba(10,12,16,0.97)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            zIndex: 99,
            padding: '24px clamp(16px, 4vw, 32px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            overflowY: 'auto',
          }}
        >
          {NAV_TABS.map(t => {
            const active = activeKey === t.key;
            return (
              <button
                key={t.key}
                onClick={() => navigate(t.path)}
                style={{
                  padding: '16px 18px',
                  borderRadius: 14,
                  background: active ? 'rgba(255,255,255,0.07)' : 'transparent',
                  border: '1px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                  fontFamily: fontStack,
                  fontSize: 16,
                  fontWeight: 500,
                  color: active ? navInk : navInkDim,
                  textAlign: 'left',
                }}
              >
                {t.label}
              </button>
            );
          })}

          <button
            onClick={() => { goAI(); setDrawerOpen(false); }}
            style={{
              padding: '16px 18px',
              borderRadius: 14,
              background: 'linear-gradient(135deg, rgba(232,68,26,0.15), rgba(75,142,219,0.15))',
              border: '1px solid rgba(255,255,255,0.1)',
              cursor: 'pointer',
              fontFamily: fontStack,
              fontSize: 16,
              fontWeight: 600,
              color: navInk,
              textAlign: 'left',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 8,
            }}
          >
            <span style={{
              width: 22, height: 22, borderRadius: 11,
              background: 'linear-gradient(135deg, #E8441A, #4B8EDB)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12,
            }}>✦</span>
            Global Flyt AI
          </button>

          {status === 'authed' && (
            <button
              onClick={() => { void logout(); setDrawerOpen(false); }}
              style={{
                padding: '16px 18px',
                borderRadius: 14,
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.08)',
                cursor: 'pointer',
                fontFamily: fontStack,
                fontSize: 14,
                color: navInkDim,
                textAlign: 'left',
                marginTop: 16,
              }}
            >
              Sign out
            </button>
          )}
        </div>
      )}
    </>
  );
}
